/**
 * Web Component: <exp-blog-sort>
 * Handles the "Sort by Newest / Oldest" dropdown and dynamically reorders blog post cards.
 */
class ExpBlogSort extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.menu = null;
    this.currentText = null;
    this.options = [];
    this.currentSort = 'newest';
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-exp-sort-trigger]');
    this.menu = this.querySelector('[data-exp-sort-menu]');
    this.currentText = this.querySelector('[data-exp-sort-current]');
    this.options = Array.from(this.querySelectorAll('[data-exp-sort-option]'));

    if (!this.trigger || !this.menu) return;

    this.bindEvents();
    this.initFromUrl();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  bindEvents() {
    this.trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleMenu();
    });

    this.options.forEach((option) => {
      option.addEventListener('click', () => {
        const sortValue = option.getAttribute('data-exp-sort-option');
        this.selectOption(sortValue);
        this.closeMenu();
      });
    });

    document.addEventListener('click', this.handleOutsideClick);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  toggleMenu() {
    const isOpen = this.menu.classList.contains('is-open');
    if (isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.menu.classList.add('is-open');
    this.trigger.setAttribute('aria-expanded', 'true');
  }

  closeMenu() {
    this.menu.classList.remove('is-open');
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  handleOutsideClick(e) {
    if (!this.contains(e.target)) {
      this.closeMenu();
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
      this.closeMenu();
      this.trigger.focus();
    }
  }

  selectOption(sortValue) {
    if (this.currentSort === sortValue) return;

    this.currentSort = sortValue;

    // Update UI
    this.options.forEach((opt) => {
      const isSelected = opt.getAttribute('data-exp-sort-option') === sortValue;
      opt.classList.toggle('is-selected', isSelected);
      opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      if (isSelected && this.currentText) {
        this.currentText.textContent = opt.querySelector('span')?.textContent?.trim() || (sortValue === 'oldest' ? 'Oldest' : 'Newest');
      }
    });

    // Update URL query string without reloading (reset page to 1 on sort change)
    const url = new URL(window.location);
    url.searchParams.set('sort', sortValue);
    url.searchParams.set('page', '1');
    window.history.replaceState({}, '', url);

    // Apply sorting to blog posts grid
    this.applySort(sortValue);

    // Dispatch custom event for any listeners
    window.dispatchEvent(new CustomEvent('exp:sort-change', { detail: { sort: sortValue, page: 1 } }));
  }

  initFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const sortParam = params.get('sort') || params.get('sort_by');
    if (sortParam === 'oldest' || sortParam === 'created-ascending') {
      this.selectOption('oldest');
    }
  }

  applySort(sortValue) {
    // Locate all matching grid containers on the page
    const containers = document.querySelectorAll(
      '.exp-blog-grid__list, [data-exp-blog-grid], .blog-posts-container, [ref="grid"], blog-posts-list .blog-posts'
    );

    containers.forEach((container) => {
      const items = Array.from(container.querySelectorAll('.exp-blog-grid__item, .blog-post-item'));
      if (!items.length) return;

      // Stamp original index and timestamp if available
      items.forEach((item, index) => {
        if (!item.hasAttribute('data-exp-orig-index')) {
          item.setAttribute('data-exp-orig-index', index.toString());
        }
      });

      // Subtle fade transition
      container.style.transition = 'opacity 0.15s ease';
      container.style.opacity = '0.5';

      setTimeout(() => {
        items.sort((a, b) => {
          const timeA = parseInt(a.getAttribute('data-published-at') || '0', 10);
          const timeB = parseInt(b.getAttribute('data-published-at') || '0', 10);

          if (timeA && timeB) {
            return sortValue === 'oldest' ? timeA - timeB : timeB - timeA;
          }

          const indexA = parseInt(a.getAttribute('data-exp-orig-index') || '0', 10);
          const indexB = parseInt(b.getAttribute('data-exp-orig-index') || '0', 10);
          return sortValue === 'oldest' ? indexB - indexA : indexA - indexB;
        });

        // Re-append items in sorted order
        items.forEach((item) => container.appendChild(item));

        // Read posts per page to show only the first page items
        const postsPerPage = parseInt(container.getAttribute('data-posts-per-page') || '4', 10);
        items.forEach((item, idx) => {
          if (idx < postsPerPage) {
            item.style.display = '';
          } else {
            item.style.display = 'none';
          }
        });

        container.style.opacity = '1';

        // Notify pagination that sort changed and page is reset to 1
        window.dispatchEvent(new CustomEvent('exp:page-change', { detail: { page: 1, sort: sortValue } }));
      }, 150);
    });
  }
}

if (!customElements.get('exp-blog-sort')) {
  customElements.define('exp-blog-sort', ExpBlogSort);
}
